import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { Popover } from "@msuite/ui/popover";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { ArticleListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { ArticleImagePopover } from "@/components/popovers/article-image-popover";
import { useCitadel } from "@/hooks/use-citadel";
import { AddVendorArticleModal } from "@/modals/stock/add-vendor-article-modal";
import { EditArticleModal } from "@/modals/stock/edit-article-modal";

export function RowActions({ cell }: CellContext<ArticleListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const editArticleModal = useDisclosure();
	const addVendorArticleModal = useDisclosure();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	const article = cell.row.original;
	// The menu is an array of items, so the gate sits at the caller rather than in
	// a `<Can>` wrapper — same three-state guard as that component documents
	// (`apps/web/src/components/citadel/can.tsx`): dropped only on a definitive
	// denial, kept while the rules load so the server has the last word.
	// Both entries write — the vendor article is a POST — so both hang off the same
	// `update Stock` the routes gate on.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const isImageDisabled = !article.hasImage;

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={`/organization/data/stock/articles/${article.id}`}>
				<Popover
					trigger="click"
					variant="plain"
					placement="left"
					isDisabled={isImageDisabled}
					render={() => (
						<ArticleImagePopover
							articleId={article.id}
							articleName={article.name}
						/>
					)}
				>
					<Button
						size="xxs"
						icon={<Icon name="Image" />}
						variant="ghost"
						isDisabled={isImageDisabled}
					/>
				</Popover>
				{!stockUpdateIsDenied && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editArticleModal.onOpen,
							items: [
								{
									label: t("stock.add_vendor_article"),
									onClick: addVendorArticleModal.onOpen,
									icon: icon("Plus"),
								},
							],
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{editArticleModal.isOpen && (
				<EditArticleModal
					isOpen={editArticleModal.isOpen}
					onClose={editArticleModal.onClose}
					onComplete={refetch}
					articleId={article.id}
				/>
			)}
			{addVendorArticleModal.isOpen && (
				<AddVendorArticleModal
					isOpen={addVendorArticleModal.isOpen}
					onClose={addVendorArticleModal.onClose}
					articleId={article.id}
				/>
			)}
		</Fragment>
	);
}
